import React from 'react'
import { Head, Link } from '@inertiajs/react'
import { Container, Typography, Box, Button, Stack, Card, Avatar } from '@mui/material'
import PlaceIcon from '@mui/icons-material/Place'
import GroupsIcon from '@mui/icons-material/Groups'
import LocationCard from '../../components/LocationCard'
import GroupChip from '../../components/GroupChip'
import SectionHeading from '../../components/SectionHeading'

interface GroupRef { id: number; name: string }
interface LocationItem {
  id: number
  name: string
  address: string | null
  category: string | null
  groups: GroupRef[]
}
interface GroupItem {
  id: number
  name: string
  gender: string
  group_type: string
  member_count: number
  company: { id: number; name: string }
}

interface Props {
  popular_locations: LocationItem[]
  featured_groups: GroupItem[]
}

export default function HomeIndex({ popular_locations, featured_groups }: Props) {
  return (
    <>
      <Head title="Home" />
      <Container maxWidth="xl" sx={{ py: { xs: 3, md: 4 }, px: { xs: 2, md: 4 } }}>
        {/* Hero */}
        <Card
          sx={{
            p: { xs: 3, md: 4 },
            mb: 4,
            background: 'linear-gradient(135deg, #EDE6FE 0%, #FAF7FF 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 3,
          }}
        >
          <Box sx={{ flex: '1 1 320px', minWidth: 0 }}>
            <Typography variant="h4" sx={{ mb: 1.25 }}>
              Discover K-pop spots ✨
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2.5 }}>
              Cafés, filming spots, fan meet-ups — find them all in one map.
            </Typography>
            <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap', gap: 1 }}>
              <Button component={Link} href="/locations" variant="contained" size="large" startIcon={<PlaceIcon />}>
                Browse Locations
              </Button>
              <Button component={Link} href="/groups" variant="outlined" color="primary" size="large" startIcon={<GroupsIcon />}>
                Browse Groups
              </Button>
            </Stack>
          </Box>
        </Card>

        {/* Popular this week */}
        <Box sx={{ mb: 5 }}>
          <SectionHeading title="Popular this week" viewAllHref="/locations" />
          {popular_locations.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No locations yet. <Link href="/locations/new" style={{ color: '#7B5BD6', fontWeight: 600 }}>Add the first one →</Link>
            </Typography>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  lg: 'repeat(3, 1fr)',
                },
              }}
            >
              {popular_locations.map((l) => (
                <LocationCard key={l.id} location={l} />
              ))}
            </Box>
          )}
        </Box>

        {/* Browse by group */}
        <Box>
          <SectionHeading title="Browse by group" viewAllHref="/groups" />
          {featured_groups.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No groups yet.</Typography>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: {
                  xs: 'repeat(2, 1fr)',
                  sm: 'repeat(3, 1fr)',
                  md: 'repeat(4, 1fr)',
                },
              }}
            >
              {featured_groups.map((g) => (
                <Card
                  key={g.id}
                  component={Link}
                  href={`/groups/${g.id}`}
                  sx={{
                    p: 2,
                    textDecoration: 'none',
                    color: 'inherit',
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1,
                    transition: 'transform 0.15s ease',
                    '&:hover': { transform: 'translateY(-2px)' },
                  }}
                >
                  <Avatar
                    sx={{
                      width: 64,
                      height: 64,
                      backgroundColor: 'rgba(123, 91, 214,0.15)',
                      color: 'primary.main',
                      fontWeight: 700,
                      fontSize: '1.4rem',
                    }}
                  >
                    {g.name.charAt(0)}
                  </Avatar>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{g.name}</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {g.member_count} members
                    </Typography>
                  </Box>
                  <GroupChip group={g} />
                </Card>
              ))}
            </Box>
          )}
        </Box>
      </Container>
    </>
  )
}
